import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import {
  Button,
  connectToLocale,
  Currency,
  Icon,
  SimpleStockInfo,
  ListPrice,
  PriceTagWithPromoMessage,
  Tooltip
} from '@insight/toolkit-react'
import { t, getVatPriceProps } from '@insight/toolkit-utils'
import { PDPContext } from '../../context'
import { Image } from '../../shared/Image/Image'
import InstantRebates from './InstantRebates'
import { getWebPricingFeatureFlag } from '../../shared'
import { ProrationMessage } from './ProrationMessage'
import useResponsive from '../../hooks/useResponsive'
import SpecificationsIPSContract from './SpecificationsIPSContracts'
import { HIDE_LOGGEDOUT_PRICING_BY_SALESORG, DISPLAY_FINANCING_OFFERS_BUTTON, OPEN_MARKET } from '../../constants'
import { ProrationTable, CCXCurrentProduct } from './ProrationTable';
import {shortTermLeasingOptionFF} from "../../shared/getFeatureFlagUtils";

function SpecificationsPricing({ availableQuantity, unlimited, selectedContract, setSelectedContract, contracts, context, isOpenMarketEnabled, openShortTermLeasingModal}) {
  const { locale, permissions, isIPSUser } = context
  const {enableViewPricing} = permissions
  const { isLoggedIn, product, showVAT } = useContext(PDPContext)
  const { price, proratable, availability, leasingPromo } = product
  const { coi, csi, cri } = availability
  const { webPrice, insightPrice, listPrice, listPriceInclVat, currency, priceLabel, webPriceInclVat} = price
  const newWebPriceFeature = getWebPricingFeatureFlag();
  const isStockAndPriceDisabled = isLoggedIn ? Insight?.webGroupPermissions?.includes('disable_stock_and_price_display') : false
  // list price was disabled in login IPS no matter the permissions and discounts, unless “US Comm Web Index / Discount“ in SAP was enabled
  const isListPriceEnable = isIPSUser && isLoggedIn ? selectedContract?.displayDiscount : isLoggedIn && Insight?.webGroupPermissions?.includes('list_price');
  const enableInventorySearch = isLoggedIn && permissions?.enable_inventory_search
  const [isMobile] = useResponsive()
  const hideLoggedOutPricing = window?.flags?.[HIDE_LOGGEDOUT_PRICING_BY_SALESORG];

  const priceLabelMap = {
    "COIPRICELABEL": "Client owned price",
    "CSIPRICELABEL": "Client supplied price",
    "RESERVEDPRICELABEL": "Your price",
    "YOURPRICELABEL": "Your price"
  }
  const bestPriceBadge = <span className="c-product-specifications__pricing__your-price__best-price">Best price</span>
  const displaySelectedContract = selectedContract?.abbreviation || selectedContract?.contractName
  const isBestPrice = (contracts?.[0]?.contractNumber === selectedContract?.contractNumber) && isOpenMarketEnabled && contracts?.length > 1;
  // display list price for all logout user and login user with list_price permission and ips user
  const displayListPrice =
    !isLoggedIn || Insight?.webGroupPermissions?.includes('list_price') || isIPSUser
  const showDiscount = (isIPSUser && selectedContract?.displayDiscount) || false
  const contractDiscount = selectedContract?.contractDiscount || 0
  const getPrice = (ipsPrice, defaultPrice) => {
    return (isIPSUser && !!selectedContract) ? ipsPrice : defaultPrice
  }

  const isLogoutIPSUser = isIPSUser && !isLoggedIn
  const isIPSUserWithContract = isIPSUser && !!displaySelectedContract
  const logoutIPSContractName = isLogoutIPSUser ? displaySelectedContract || OPEN_MARKET.toUpperCase() : null
  const logoutIPSPriceLabel = isLogoutIPSUser ? (isIPSUserWithContract ? 'Contract price:' : 'Open market price:') : null
  const logoutIPSPriceInfo = isIPSUserWithContract
    ? "Displayed price reflects standard contract terms. For the most accurate pricing tied to your organization's agreement, please log in."
    : "Displayed price reflects the Insight standard customer price. For the most accurate pricing tied to your organization's agreement, please log in."

  const isCCX = product?.materialQualifier === 'CCX' && product?.subscriptionInfo != null;
  const hasCcxLevels = product?.subscriptionInfo?.levels?.length > 0;
  const ccxHasFailureReason = product?.subscriptionInfo?.failureReason?.length > 0;
  // Select manuafacturer image
  const manufacturerImage = product.manufacturer?.images?.length
    ? product.manufacturer?.images[0]
    : false

  const renderAvailability = () => {
    const isAvailable = product.availability?.totalQuantity ? true : false
    return (
      <p>
        Get it as soon as:{' '}
        <span className="availability-message">
          {isAvailable
            ? product.availabilityMessage
            : product.unavailabilityMessage}
        </span>
        {!isAvailable && '(estimated)'}
      </p>
    )
  }

  const renderPricingLabel = () => {
    if (logoutIPSContractName) {
      return `${t(logoutIPSPriceLabel)}`;
    }
    return enableInventorySearch && !!priceLabel
      ? `${t(priceLabelMap[priceLabel])}:`
      : (newWebPriceFeature || isLoggedIn)
        ? `${t('Your price:')}` : `${t('List price:')}`;
  }

  // check for view pricing permission
  if(!enableViewPricing) {
    return null
  }
  const shorTermLeasingOption = () => {
    const isAppleProduct = product.manufacturer?.name?.toUpperCase() === 'APPLE';
    const isB2B = window?.Insight?.userInformation?.UserType === 'B2B';
    const isAllowedPartnerProgram = ['FPP',undefined].includes(window.Insight?.userInformation?.partnerProgramName);
    // Show short-term leasing to logged-out users (public offer).
    // Logged-in users must have DISPLAY_FINANCING_OFFERS_BUTTON permission to see this option.
    const hasShortTermLeasingAccess = !isLoggedIn ||
                                           window.Insight?.webGroupPermissions?.includes(DISPLAY_FINANCING_OFFERS_BUTTON);
    return (
      isAppleProduct && shortTermLeasingOptionFF() && !isB2B && isAllowedPartnerProgram &&
         hasShortTermLeasingAccess &&
      <div className='c-product-specifications__leasing'>
          <span className='c-button c-button--inline-link leasing-link' onClick={openShortTermLeasingModal}>
            {t('Financing offers available')}
          </span>
          <span className="small-spacer">&nbsp;</span>
        {leasingPromo &&
          <span className="c-list-item__product-details__leasing promo-message">
            <PriceTagWithPromoMessage discount={leasingPromo.discount} promoMessage={t("leasing savings")}/>
          </span>
        }
      </div>
    )
  }

  // Check if call for price
  if ((product.price?.callForPrice) || (isCCX && ccxHasFailureReason)) {
    return (
      <>
        <section className="c-product-specifications__pricing__call-for-price u-text-bold m-bottom">
          {t('Contact us for availability & pricing')}
        </section>
        {shorTermLeasingOption()}
      </>
    )
  }
  const { exclVatProps, inclVatProps } = getVatPriceProps(showVAT);
  const contractNameToDisplay = isIPSUser && isLoggedIn ? displaySelectedContract : logoutIPSContractName;
  return !isStockAndPriceDisabled && (
    <section className={cn("c-product-specifications__pricing o-grid",
      { 'c-product-specifications__pricing__vat': showVAT }
    )}>
      <div className="pricing-left-col u-4/5@tablet">
        <div className="o-grid__item">
          {contractNameToDisplay && (
            <div className="c-product-specifications__pricing__selected-contract o-grid">
              <span className="u-text-bold">{contractNameToDisplay}</span>
            </div>
          )}
          {(!hideLoggedOutPricing && !isCCX) && <div className="c-product-specifications__pricing__price o-grid">
            <div className="c-product-specifications__pricing__your-price o-grid__item u-1/1">
              <div>
                <div className='c-product-specifications__product-price'>
                  <span className="u-text-bold">
                    {renderPricingLabel()}
                  </span>
                  <Currency
                    currencyCode={currency}
                    value={getPrice(selectedContract?.webPrice, webPrice)}
                    size="large"
                    className='c-product-specifications__pricing__your-price__value'
                    {...exclVatProps}
                  />
                  {isLogoutIPSUser ? (
                    <span className={"c-logout-ips-price u-text-bold"}>
                      <Tooltip content={t(logoutIPSPriceInfo)} className={"c-logout-ips-price-info-tooltip"}>
                        <Icon
                          color="primary"
                          icon="information-circled"
                          type="info"
                        />
                      </Tooltip>
                    </span>
                  ) : null}
                  {isBestPrice && bestPriceBadge}
                </div>
                <div className='c-product-specifications__product-price'>
                  {showVAT &&
                    <div>
                        <span className='o-grid o-grid--justify-right'>
                          <Currency
                            value={webPriceInclVat}
                            currencyCode={currency}
                            size="large"
                            className="c-currency__price-value"
                            {...inclVatProps}
                          />
                        </span>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div> }
          {(displayListPrice && !hideLoggedOutPricing && !isCCX) &&
            <div className="c-product-specifications__pricing__price o-grid__item u-1/1">
              <ListPrice
                currencyCode={currency}
                listPrice={listPrice}
                insightPrice={getPrice(selectedContract?.webPrice, webPrice)}
                showListPriceLabel
                strike
                showHorizontalSaveTag
                showSaving={newWebPriceFeature}
                valueClassName='c-product-specifications__listprice'
                labelClassName='u-text-bold'
                showDiscount={showDiscount}
                discount={contractDiscount}
                indexPrice={selectedContract?.indexPrice}
                isIPSUser={isIPSUser}
                {...exclVatProps}
              />
            </div>
          }
          {(contracts?.length > 1 && selectedContract && isIPSUser && !isCCX) && (
            <div className="c-product-specifications__pricing__ips-contracts o-grid__item u-1/1">
              <SpecificationsIPSContract
                prices={contracts}
                selectedContract={selectedContract}
                setSelectedContract={setSelectedContract}
                locale={locale}
                isMobile={isMobile}
              />
            </div>
          )}
          {shorTermLeasingOption()}
          {!isCCX && (
            <div className="c-product-specifications__pricing__stock o-grid">
              <div>
                <div className="o-grid c-product-specifications__pricing__inventory">
                  <SimpleStockInfo
                    stock={availableQuantity}
                    unlimited={unlimited}
                    showLimitedStockQty
                    showBackOrder={true}
                    isPDP={true}
                  />
                  {!!coi && <span>&nbsp;{`| ${t('Client owned')}: ${coi}`}</span>}
                  {!!csi && <span>&nbsp;{`| ${t('Client supplied')}: ${csi}`}</span>}
                  {!!cri && <span>&nbsp;{`| ${t("Reserved")}: ${cri}`}</span>}
                </div>
                {availableQuantity <= 0 ? (
                  <div className="c-product-specifications__pricing__stock-message">
                    {t("This item will ship once it's back in stock.")}
                  </div>
                ) : null}
              </div>
              {proratable && <ProrationMessage />}
            </div>
          )}
          {isCCX && hasCcxLevels && <CCXCurrentProduct ccxProduct={product?.subscriptionInfo} currency={currency} />}
          {/* TODO:API waiting on availability message*/}
          {/* <div className="o-grid__item u-1/1">{renderAvailability()}</div> */}
        </div>
      </div>
      <div className="o-grid__item u-1/5@tablet">
        {manufacturerImage ? (
          <div className="c-product-specifications__pricing__brand-logo">
            <Button
              color="none"
              aria-label={t('Go to brand category page')}
              href={`/${locale}/search.html?qtype=all&q=${encodeURIComponent(
                product.manufacturer?.name?.toLowerCase()
              )}&qsrc=k`}
            >
              <Image
                ariaHidden="true"
                src={manufacturerImage.url}
                alt={manufacturerImage.description}
              />
            </Button>
          </div>
        ) : null}
        {/* TODO:Phase 2 - Display promotional content */}

        {!isLoggedIn ? <InstantRebates /> : null}

        {product.promotion ? (
          <div className="c-product-specifications__pricing__promotion">
            <Icon
              icon="information-circled"
              className="c-product-specifications__pricing__ribbon"
              size={28}
            />
            <span>{product.promotion}</span>
          </div>
        ) : null}
      </div>
      {isCCX && <ProrationTable ccxProduct={product?.subscriptionInfo} currency={currency} />}
    </section>
  )
}

SpecificationsPricing.propTypes = {
  availableQuantity: PropTypes.number.isRequired,
  openShortTermLeasingModal: PropTypes.func.isRequired,
}

export default connectToLocale(SpecificationsPricing)
